import { forwardRef, useCallback } from "react";
import { Link as RRLink, useNavigate as useRRNavigate, useLocation } from "react-router-dom";
import { splitLangPrefix, buildLocalizedPath } from "./langRouting";

function shouldSkip(to) {
  if (typeof to !== "string") return true;
  if (/^([a-z][a-z0-9+.-]*:|\/\/|#)/i.test(to)) return true; // http:, mailto:, //, #anchor
  if (to.startsWith("/#")) return true; // In-Page-Hash-Ziel, z.B. "/#upload"
  if (!to.startsWith("/")) return true; // relative Navigation ("edit", "..")
  return false;
}

function localizePath(to, currentLang) {
  if (shouldSkip(to)) return to;
  const { basePath } = splitLangPrefix(to); // strippt vorhandenes Präfix (Doppel-Präfix-Schutz)
  return buildLocalizedPath(currentLang, basePath);
}

function useCurrentLang() {
  const { pathname } = useLocation();
  return splitLangPrefix(pathname).lang;
}

export const Link = forwardRef(function Link({ to, ...rest }, ref) {
  const currentLang = useCurrentLang();
  const localTo = typeof to === "object" && to && "pathname" in to
    ? { ...to, pathname: localizePath(to.pathname, currentLang) }
    : localizePath(to, currentLang);
  return <RRLink ref={ref} to={localTo} {...rest} />;
});

export function useNavigate() {
  const rrNavigate = useRRNavigate();
  const currentLang = useCurrentLang();
  // useCallback ist hier kein Performance-Detail, sondern Korrektheit: React Routers
  // eigenes useNavigate() liefert eine stabile Referenz, die z.B. gefahrlos in
  // useEffect-Dependency-Arrays landet. Ohne Memoization hier würde eine neue
  // Funktion pro Render entstehen → useEffect(() => {...}, [navigate]) liefe bei
  // jedem Render neu → Endlosschleife, sobald der Effect selbst navigate() aufruft
  // (konkret in FartPage.js beobachtet: fetch → navigate(slug-redirect) → Re-Render
  // → neue navigate-Referenz → Effect erneut → fetch erneut → ...).
  return useCallback((to, options) => {
    if (typeof to === "number") return rrNavigate(to); // navigate(-1) passthrough
    if (to && typeof to === "object" && "pathname" in to) {
      return rrNavigate({ ...to, pathname: localizePath(to.pathname, currentLang) }, options);
    }
    return rrNavigate(localizePath(to, currentLang), options);
  }, [rrNavigate, currentLang]);
}

// Nicht-Hook-Variante für Code außerhalb von React-Komponenten (z.B. api.js)
export function localizedHref(to) {
  if (typeof window === "undefined") return to;
  const { lang } = splitLangPrefix(window.location.pathname);
  return localizePath(to, lang);
}
